import { useMemo } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import {
  ArrowLeft,
  ChevronDown,
  ChevronUp,
  Info,
  Minus,
  Plus,
} from "lucide-react";

import { useApp } from "@/context/AppContext";
import { MixOverridesEditor } from "@/components/MixOverridesEditor";
import {
  STRATEGY_META,
  type BundleSortMode,
  type BundleStrategyId,
  type BundleStrategySetting,
  type MixOverride,
  type WeightProfileName,
} from "@/features/bundleEngine";
import {
  computeBundlePerformance,
  type BundlePerformanceStat,
} from "@/features/bundlePerformance";
import { ManagerOnly } from "@/components/ManagerOnly";

export const Route = createFileRoute("/settings/bundle-defaults")({
  component: () => (
    <ManagerOnly>
      <BundleSettingsPage />
    </ManagerOnly>
  ),
});

function Toggle({
  checked,
  onChange,
  disabled,
}: {
  checked: boolean;
  onChange: () => void;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      onClick={onChange}
      disabled={disabled}
      className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors disabled:opacity-40 ${
        checked ? "bg-secondary" : "bg-muted"
      }`}
    >
      <span
        className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform ${
          checked ? "translate-x-5" : "translate-x-0.5"
        }`}
      />
    </button>
  );
}

const SORT_OPTIONS: { id: BundleSortMode; label: string }[] = [
  { id: "profit", label: "Max Profit" },
  { id: "speed", label: "Fastest Sales" },
  { id: "balanced", label: "Balanced" },
];

function BundleSettingsPage() {
  const { cards, products, sales, bundleSettings, updateBundleSettings } =
    useApp();
  const settings = bundleSettings;

  const performanceMap = useMemo(() => {
    const map = new Map<BundleStrategyId, BundlePerformanceStat>();
    for (const stat of computeBundlePerformance(products, sales)) {
      map.set(stat.strategyId, stat);
    }
    return map;
  }, [products, sales]);

  const hasPokemonApiData = cards.some(
    (c) =>
      c.game === "Pokemon" &&
      (c.tcgTypes?.length || c.tcgSupertype || c.tcgSeries),
  );

  const orderedStrategies = [...settings.strategies].sort(
    (a, b) => a.priority - b.priority,
  );

  const toggleGlobal = () =>
    updateBundleSettings({
      ...settings,
      globalEnabled: !settings.globalEnabled,
    });

  const toggleStrategy = (id: BundleStrategyId) =>
    updateBundleSettings({
      ...settings,
      strategies: settings.strategies.map((s) =>
        s.id === id ? { ...s, enabled: !s.enabled } : s,
      ),
    });

  const moveStrategy = (id: BundleStrategyId, direction: "up" | "down") => {
    const sorted = [...settings.strategies].sort(
      (a, b) => a.priority - b.priority,
    );
    const idx = sorted.findIndex((s) => s.id === id);
    const targetIdx = direction === "up" ? idx - 1 : idx + 1;
    if (targetIdx < 0 || targetIdx >= sorted.length) return;
    const updated = sorted.map((s, i): BundleStrategySetting => {
      if (i === idx) return { ...s, priority: targetIdx };
      if (i === targetIdx) return { ...s, priority: idx };
      return s;
    });
    updateBundleSettings({ ...settings, strategies: updated });
  };

  const setSortMode = (mode: BundleSortMode) => {
    if (mode === settings.sortMode) return;
    updateBundleSettings({ ...settings, sortMode: mode });
  };

  const adjustMinCards = (delta: number) => {
    const next = Math.min(50, Math.max(5, settings.sameSetMinCards + delta));
    updateBundleSettings({ ...settings, sameSetMinCards: next });
  };

  const updateMixOverrides = (
    next: Partial<Record<WeightProfileName, MixOverride>>,
  ) => updateBundleSettings({ ...settings, mixOverrides: next });

  const universalStrategies = orderedStrategies.filter((s) => {
    const meta = STRATEGY_META.find((m) => m.id === s.id);
    return meta && !meta.requiresPokemonApiData;
  });
  const pokemonStrategies = orderedStrategies.filter((s) => {
    const meta = STRATEGY_META.find((m) => m.id === s.id);
    return meta?.requiresPokemonApiData;
  });

  const renderStrategyRow = (
    strategy: BundleStrategySetting,
    idx: number,
    total: number,
  ) => {
    const meta = STRATEGY_META.find((m) => m.id === strategy.id);
    if (!meta) return null;
    const isFirst = idx === 0;
    const isLast = idx === total - 1;
    const perf = performanceMap.get(strategy.id);
    const active = settings.globalEnabled && strategy.enabled;

    return (
      <div
        key={strategy.id}
        className={`flex items-center gap-2.5 px-3 py-3 ${
          idx < total - 1 ? "border-b border-border" : ""
        }`}
      >
        <div className="flex flex-col">
          <button
            type="button"
            onClick={() => moveStrategy(strategy.id, "up")}
            disabled={isFirst || !settings.globalEnabled}
            className="p-0.5 text-foreground disabled:opacity-25"
          >
            <ChevronUp className="h-4 w-4" />
          </button>
          <button
            type="button"
            onClick={() => moveStrategy(strategy.id, "down")}
            disabled={isLast || !settings.globalEnabled}
            className="p-0.5 text-foreground disabled:opacity-25"
          >
            <ChevronDown className="h-4 w-4" />
          </button>
        </div>

        <div className="min-w-0 flex-1 space-y-0.5">
          <div
            className={`text-sm font-semibold ${
              active ? "text-foreground" : "text-muted-foreground"
            }`}
          >
            {meta.name}
          </div>
          <div className="text-[11px] leading-snug text-muted-foreground">
            {meta.description}
          </div>

          {perf && perf.productCount > 0 && (
            <div className="mt-1.5 inline-flex items-center gap-1.5 rounded bg-muted px-1.5 py-0.5 text-[10px] font-semibold">
              <span className="text-muted-foreground">
                {perf.soldCount}/{perf.productCount} sold
              </span>
              <span className="text-muted-foreground">·</span>
              <span
                className={
                  perf.avgRoi == null
                    ? "text-muted-foreground"
                    : perf.avgRoi >= 0
                      ? "text-emerald-600"
                      : "text-destructive"
                }
              >
                ROI{" "}
                {perf.avgRoi == null
                  ? "—"
                  : `${perf.avgRoi >= 0 ? "+" : ""}${perf.avgRoi.toFixed(0)}%`}
              </span>
              <span className="text-muted-foreground">·</span>
              <span className="text-foreground">
                {perf.avgDaysToSell == null
                  ? "—"
                  : `${perf.avgDaysToSell.toFixed(1)}d`}
              </span>
            </div>
          )}

          {strategy.id === "same-set" && (
            <div className="mt-1.5 flex items-center gap-1.5">
              <span className="text-[11px] font-medium text-muted-foreground">
                Min cards:
              </span>
              <button
                type="button"
                onClick={() => adjustMinCards(-5)}
                disabled={settings.sameSetMinCards <= 5}
                className="flex h-[22px] w-[22px] items-center justify-center rounded bg-muted text-foreground disabled:opacity-40"
              >
                <Minus className="h-3 w-3" />
              </button>
              <span className="min-w-[24px] text-center text-[13px] font-bold text-foreground">
                {settings.sameSetMinCards}
              </span>
              <button
                type="button"
                onClick={() => adjustMinCards(5)}
                disabled={settings.sameSetMinCards >= 50}
                className="flex h-[22px] w-[22px] items-center justify-center rounded bg-muted text-foreground disabled:opacity-40"
              >
                <Plus className="h-3 w-3" />
              </button>
            </div>
          )}
        </div>

        <Toggle
          checked={active}
          onChange={() => toggleStrategy(strategy.id)}
          disabled={!settings.globalEnabled}
        />
      </div>
    );
  };

  const dimmed = settings.globalEnabled ? "" : "opacity-50";

  return (
    <section className="flex h-full flex-col">
      <div className="border-b border-border pb-3">
        <Link
          to="/tools"
          className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="h-4 w-4" /> Tools
        </Link>
      </div>

      <div className="flex-1 space-y-3 overflow-y-auto py-4 pr-2">
        <h1 className="text-xl font-bold text-foreground">
          Bundle suggestions
        </h1>

        <div className="flex items-center gap-3 rounded-md border border-border bg-card p-3.5">
          <div className="flex-1 space-y-1">
            <div className="text-[15px] font-semibold text-foreground">
              Bundle Suggestions
            </div>
            <div className="text-xs leading-snug text-muted-foreground">
              Analyzes your unlisted inventory and suggests ready-to-build
              bundles when creating a product.
            </div>
          </div>
          <Toggle checked={settings.globalEnabled} onChange={toggleGlobal} />
        </div>

        <div
          className={`space-y-2.5 rounded-md border border-border bg-card p-3.5 ${dimmed}`}
        >
          <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
            Optimise for
          </div>
          <div className="flex gap-1 rounded-md bg-muted p-1">
            {SORT_OPTIONS.map((opt) => {
              const sel = settings.sortMode === opt.id;
              return (
                <button
                  key={opt.id}
                  type="button"
                  onClick={() => setSortMode(opt.id)}
                  disabled={!settings.globalEnabled}
                  className={`flex-1 rounded px-2 py-2 text-xs font-semibold ${
                    sel
                      ? "bg-primary text-primary-foreground"
                      : "text-muted-foreground"
                  }`}
                >
                  {opt.label}
                </button>
              );
            })}
          </div>
          <p className="text-[11px] leading-snug text-muted-foreground">
            Controls how Bundle Idea cards are ranked on the Build Product and
            Product Detail screens. Changes apply instantly.
          </p>
        </div>

        <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
          Universal — all games
        </div>
        <div
          className={`overflow-hidden rounded-md border border-border bg-card ${dimmed}`}
        >
          {universalStrategies.map((s, i) =>
            renderStrategyRow(s, i, universalStrategies.length),
          )}
        </div>

        <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
          Pokémon — requires API lookup data
        </div>
        {!hasPokemonApiData && (
          <div className="flex items-start gap-2 rounded-md bg-muted p-3 text-[12px] leading-snug text-muted-foreground">
            <Info className="mt-0.5 h-3.5 w-3.5 shrink-0" />
            <span>
              No Pokémon cards with API data found. Use the TCG card lookup when
              adding cards to populate type, supertype, and series data.
            </span>
          </div>
        )}
        <div
          className={`overflow-hidden rounded-md border border-border bg-card ${dimmed}`}
        >
          {pokemonStrategies.map((s, i) =>
            renderStrategyRow(s, i, pokemonStrategies.length),
          )}
        </div>

        <p className="text-center text-[11px] leading-snug text-muted-foreground">
          Strategies run top-to-bottom. Each card is claimed by the first
          matching strategy — it won't appear in lower-priority bundles.
        </p>

        <MixOverridesEditor
          mixOverrides={settings.mixOverrides ?? {}}
          onChange={updateMixOverrides}
        />
      </div>
    </section>
  );
}
